import { useState, useEffect } from 'react'

export interface AdminSchool {
  id: string
  name: string
  logoUrl: string | null
  logoDisplayMode: 'both' | 'school_only' | 'system'
  // 管理者用の学校（schools.is_admin_school）。これを選んでいる間だけ、アントレ部宿題を
  // 全校横断で見られる。各校を選んでいる間はその学校の生徒しか見えない。
  isAdminSchool: boolean
}

const STORAGE_KEY = 'adminSelectedSchool'

const LOGO_DISPLAY_MODES: AdminSchool['logoDisplayMode'][] = ['both', 'school_only', 'system']

// The selection is kept in sessionStorage so a reload doesn't drop an admin back to
// "すべての学校", while each tab keeps its own school. sessionStorage throws in some
// privacy modes, and this runs at module load, so every access has to be guarded.
const readStoredSchool = (): AdminSchool | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<AdminSchool>
    if (typeof parsed?.id !== 'string' || !parsed.id) return null
    if (typeof parsed.name !== 'string' || !parsed.name) return null
    return {
      id: parsed.id,
      name: parsed.name,
      logoUrl: typeof parsed.logoUrl === 'string' ? parsed.logoUrl : null,
      logoDisplayMode: LOGO_DISPLAY_MODES.includes(parsed.logoDisplayMode as AdminSchool['logoDisplayMode'])
        ? (parsed.logoDisplayMode as AdminSchool['logoDisplayMode'])
        : 'both',
      // このフィールドが無い時代の保存値が残っていても「管理者用ではない」に倒す
      isAdminSchool: parsed.isAdminSchool === true,
    }
  } catch {
    return null
  }
}

const storeSchool = (school: AdminSchool | null): void => {
  try {
    if (school) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(school))
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // Persistence is a convenience; a storage failure must not break the selection.
  }
}

let _adminSelectedSchool: AdminSchool | null = readStoredSchool()

export const getAdminSelectedSchool = (): AdminSchool | null => _adminSelectedSchool

export const setAdminSelectedSchool = (school: AdminSchool | null): void => {
  _adminSelectedSchool = school
  storeSchool(school)
  window.dispatchEvent(new CustomEvent('adminSchoolChanged', { detail: school }))
}

export const notifySchoolLogoUpdated = (
  schoolId: string,
  logoUrl: string | null,
  logoDisplayMode?: AdminSchool['logoDisplayMode']
): void => {
  window.dispatchEvent(new CustomEvent('schoolLogoUpdated', { detail: { schoolId, logoUrl, logoDisplayMode } }))
}

export const useAdminSelectedSchool = (): AdminSchool | null => {
  const [school, setSchool] = useState<AdminSchool | null>(_adminSelectedSchool)
  useEffect(() => {
    const handler = (e: Event) => setSchool((e as CustomEvent<AdminSchool | null>).detail)
    window.addEventListener('adminSchoolChanged', handler)
    return () => window.removeEventListener('adminSchoolChanged', handler)
  }, [])
  return school
}
